import { clsx } from 'clsx';

export interface BadgeProps {
  children: string;
  variant?: 'primary' | 'warning' | 'error' | 'success' | 'info';
  className?: string;
}

/**
 * This component supports various CSS variables for theming. Here's a comprehensive list, along
 * with their default values:
 *
 * ```css
 * :root {
 *   --badge-primary-background: color-mix(in oklab, hsl(var(--primary)), white 75%);
 *   --badge-accent-background: hsl(var(--accent));
 *   --badge-success-background: color-mix(in oklab, hsl(var(--success)), white 75%);
 *   --badge-warning-background: color-mix(in oklab, hsl(var(--warning)), white 75%);
 *   --badge-error-background: color-mix(in oklab, hsl(var(--error)), white 75%);
 *   --badge-info-background: color-mix(in oklab, hsl(var(--background)), black 5%);
 *   --badge-text: hsl(var(--foreground));
 *   --badge-font-family: var(--font-family-mono);
 * }
 * ```
 *
 * Note: Uses subtle rounded corners per brand guidelines - no pill shapes allowed
 */
export function Badge({ children, className, variant = 'primary' }: BadgeProps) {
  return (
    <span
      className={clsx(
        'rounded-md px-2 py-0.5 font-[family-name:var(--badge-font-family,var(--font-family-mono))] text-xs uppercase tracking-tighter text-(--badge-text,hsl(var(--foreground)))',
        {
          primary:
            'bg-(--badge-primary-background,color-mix(in_oklab,hsl(var(--primary)),white_75%))',
          warning:
            'bg-(--badge-warning-background,color-mix(in_oklab,hsl(var(--warning)),white_75%))',
          error:
            'bg-(--badge-error-background,color-mix(in_oklab,hsl(var(--error)),white_75%))',
          success:
            'bg-(--badge-success-background,color-mix(in_oklab,hsl(var(--success)),white_75%))',
          info: 'bg-(--badge-info-background,color-mix(in_oklab,hsl(var(--background)),black_5%))',
        }[variant],
        className,
      )}
    >
      {children}
    </span>
  );
}
